import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosAPI from "../../../axiosAPI";

// === NDJSON streaming helper (fetch + ReadableStream) ===
async function streamNDJSON({ url, init, onLine }) {
  const res = await fetch(url, init);
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 1);

      if (!line) continue;
      // sentinela
      if (line === '[DONE]') return;

      try {
        const json = JSON.parse(line);
        onLine?.(json);
      } catch {
        // ignora linha parcial
      }
    }
  }
}

function getBaseURL() {
  const base = axiosAPI.defaults.baseURL || "";
  return String(base).replace(/\/+$/, "");
}

function getAuthHeader() {
  const bearer = axiosAPI.defaults.headers?.Authorization;
  
  return bearer ? { Authorization: bearer } : {};
}

export const OpenAIAPI = {
  listChats: async () => {
    const { data } = await axiosAPI.get("/openai/chats");
    return data;
  },
  getChat: async (chatId) => {
    const { data } = await axiosAPI.get(`/openai/chats/${chatId}`);
    return data;
  },
  startChat: async ({ question, type, title }) => {
    const { data } = await axiosAPI.post("/openai/chats", {
      question,
      type,
      title,
    });
    return data;
  },
  sendMessage: async (chatId, { message }) => {
    const { data } = await axiosAPI.post(`/openai/chats/${chatId}/messages`, {
      message,
    });
    return data;
  },
  deleteChat: async (chatId) => {
    await axiosAPI.delete(`/openai/chats/${chatId}`);
    return chatId;
  },
  // ========= NOVO: STREAM =========
  startChatStream: async ({ question, type, title, onEvent, onDelta }) => {
    const base = getBaseURL();
    await streamNDJSON({
      url: `${base}/openai/chats/stream`,
      init: {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/x-ndjson",
          ...getAuthHeader(),
        },
        body: JSON.stringify({ question, type, title }),
      },
      onLine: (evt) => {
        if (evt.delta) onDelta?.(evt.delta);
        else onEvent?.(evt);
      },
    });
  },
  sendMessageStream: async (chatId, { message, onEvent, onDelta }) => {
    const base = getBaseURL();
    await streamNDJSON({
      url: `${base}/openai/chats/${chatId}/messages/stream`,
      init: {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/x-ndjson",
          ...getAuthHeader(),
        },
        body: JSON.stringify({ message }),
      },
      onLine: (evt) => {
        if (evt.delta) onDelta?.(evt.delta);
        else onEvent?.(evt);
      },
    });
  },
};

// Anexa 'chunk' no último assistant "pendente" (ou último assistant)
export function appendAssistantDeltaToCache(qc, chatId, chunk) {
  if (!chatId) return;
  const key = ["chat", chatId];
  const data = qc.getQueryData(key);
  if (!data || !Array.isArray(data.messages)) return;

  const msgs = [...data.messages];
  // procura o último assistant pendente; se não achar, último assistant comum
  let idx = -1;
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    if (m?.role === "assistant") { idx = i; break; }
  }
  if (idx === -1) {
    // recria a bolha pendente se não existir (ex.: refetch sobrescreveu)
    msgs.push({
      id: `stream-assistant-${Date.now()}`,
      role: "assistant",
      content: "",
      status: "pending",
      __optimistic: true,
    });
    idx = msgs.length - 1;
  }

  const target = { ...msgs[idx] };
  target.content = (target.content || "") + chunk;
  // enquanto streama, marca status= "pending"
  target.status = "pending";
  msgs[idx] = target;

  qc.setQueryData(key, { ...data, messages: msgs });
}

// Ao finalizar, marca último assistant como ok
export function finalizeAssistantInCache(qc, chatId) {
  if (!chatId) return;
  const key = ["chat", chatId];
  const data = qc.getQueryData(key);
  if (!data || !Array.isArray(data.messages)) return;

  const msgs = [...data.messages];
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i]?.role === "assistant") {
      msgs[i] = { ...msgs[i], status: "ok" };
      break;
    }
  }
  qc.setQueryData(key, { ...data, messages: msgs });
}

export function pickListData(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

export function titleFromQuestion(q) {
  if (!q) return "Nova conversa";
  const t = q.trim().replace(/\s+/g, " ");
  return t.length > 60 ? t.slice(0, 57) + "…" : t || "Nova conversa";
}

export function useChats() {
  return useQuery({
    queryKey: ["chats"],
    queryFn: OpenAIAPI.listChats,
    refetchOnWindowFocus: false,
  });
}

export function useChat(chatId) {
  return useQuery({
    queryKey: ["chat", chatId],
    enabled: !!chatId,
    queryFn: () => OpenAIAPI.getChat(chatId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
    staleTime: 60_000,
  });
}

export function useStartChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: OpenAIAPI.startChat,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["chats"] });
      if (data?.chat?.id) {
        qc.setQueryData(["chat", data.chat.id], data.chat);
      }
    },
  });
}

export function useSendMessage(chatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => OpenAIAPI.sendMessage(chatId, payload),
    onMutate: async (payload) => {
      if (!chatId) return;
      await qc.cancelQueries({ queryKey: ["chat", chatId] });

      const previous = qc.getQueryData(["chat", chatId]);
      const now = Date.now();
      const tempUserId = `temp-user-${now}`;
      const tempAssistId = `temp-assistant-${now}`;

      const next = previous
        ? {
            ...previous,
            messages: [
              ...(previous.messages ?? []),
              {
                id: tempUserId,
                role: "user",
                content: payload.message,
                status: "ok",
                __optimistic: true,
              },
              {
                id: tempAssistId,
                role: "assistant",
                content: "",
                status: "pending",
                __optimistic: true,
              },
            ],
          }
        : previous;

      if (next) qc.setQueryData(["chat", chatId], next);

      return { previous };
    },
    onError: (error, _payload, context) => {
      if (context?.previous && chatId) {
        qc.setQueryData(["chat", chatId], context.previous);
      }
    },
    onSuccess: (data) => {
      if (data?.chat?.id) {
        qc.setQueryData(["chat", data.chat.id], data.chat);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });
}

export function useDeleteChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: OpenAIAPI.deleteChat,
    onSuccess: (chatId) => {
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.removeQueries({ queryKey: ["chat", chatId] });
    },
  });
}